import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ConfigProvider, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { DraggableSyntheticListeners } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Bars3Icon,
  CheckIcon,
  Cog6ToothIcon,
} from '@heroicons/react/24/outline';
import TextWinds from '../../common/TextWinds';
import ActionButton from '../ActionButton';
import HFbutton from '../../button/HFbutton';
import { LmsClassGroupType } from '../../../constants/types/lms-type';
import { updateClassGroupOrder } from '../../../api/utils/lms/class-group/api';
import { getErrorMessages } from '../../../api/utils/util-func';
import { useAppDispatch } from '../../../store/hook';
import { openAlert } from '../../../store/features/modalSlice';
import ClassGroupDetailDialog from './ClassGroupDetailDialog';

interface ClassGroupListCardProps {
  classId: string;
  classGroups: LmsClassGroupType[] | undefined;
  loadFailed: boolean;
  reloadClassGroups: () => void;
}

/**
 * 활성 반을 앞쪽에, 비활성 반을 뒤쪽에 모으고, 각 그룹 안에서는 displayOrder 오름차순으로 세운다.
 * 화면에서는 활성/비활성을 각각 별도 표로 나눠 그린다.
 */
const sortGroups = (groups: LmsClassGroupType[]) =>
  [...groups].sort((a, b) => {
    if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;
    return a.displayOrder - b.displayOrder;
  });

// ── 드래그 정렬(@dnd-kit) ─────────────────────────────────────────────
// 활성 반 표를 감싸는 DndContext / SortableContext 안에서만 동작한다.

/** 한 행이 열어 두는 드래그 핸들 정보. 핸들 버튼에서 꺼내 쓴다. */
interface RowContextValue {
  setActivatorNodeRef?: (element: HTMLElement | null) => void;
  listeners?: DraggableSyntheticListeners;
  canDrag: boolean;
}
const RowContext = createContext<RowContextValue>({ canDrag: false });

/** 활성 반 표가 순서 변경 모드인지 행 컴포넌트에 내려 준다. */
const ListContext = createContext<{ canReorder: boolean }>({
  canReorder: false,
});

/** 순서 열에 놓는 드래그 손잡이. 이 버튼에서만 드래그가 시작된다. */
const DragHandle = () => {
  const { setActivatorNodeRef, listeners, canDrag } = useContext(RowContext);
  return (
    <button
      type="button"
      ref={setActivatorNodeRef}
      aria-label="행 순서 이동"
      disabled={!canDrag}
      className={`flex touch-none items-center justify-center ${
        canDrag ? 'cursor-grab text-gray6' : 'cursor-not-allowed text-gray3'
      }`}
      {...(canDrag ? listeners : {})}
    >
      <Bars3Icon className="h-4 w-4" />
    </button>
  );
};

/** antd Table 의 tbody 행. useSortable 로 드래그 위치를 잡는다. (활성 반 표 전용) */
const SortableRow = (
  props: React.HTMLAttributes<HTMLTableRowElement> & {
    'data-row-key'?: number;
  },
) => {
  const { canReorder } = useContext(ListContext);
  const rowKey = props['data-row-key'];
  const disabled = !canReorder || rowKey === undefined;

  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
    // SortableContext 의 items 와 같은 형태(문자열)로 맞춘다.
  } = useSortable({
    id: rowKey === undefined ? 'row' : String(rowKey),
    disabled,
  });

  const style: React.CSSProperties = {
    ...props.style,
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isDragging
      ? {
          position: 'relative',
          zIndex: 10,
          background: '#fff',
          boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
        }
      : {}),
  };

  const rowContext = useMemo<RowContextValue>(
    () => ({ setActivatorNodeRef, listeners, canDrag: !disabled }),
    [setActivatorNodeRef, listeners, disabled],
  );

  return (
    <RowContext.Provider value={rowContext}>
      <tr {...props} ref={setNodeRef} style={style} {...attributes} />
    </RowContext.Provider>
  );
};

/** 반 관리 > 등록된 반 수정/삭제 카드 */
const ClassGroupListCard = ({
  classId,
  classGroups,
  loadFailed,
  reloadClassGroups,
}: ClassGroupListCardProps) => {
  const dispatch = useAppDispatch();

  // 반이 많아지면 이 영역 안에서만 세로로 스크롤된다.
  const scrollRef = useRef<HTMLDivElement>(null);

  // 피씨(넓은) 화면에서는 반 설명이 별도 열로 보인다. tailwind xl(1280px) 기준과 맞춘다.
  const [isWide, setIsWide] = useState(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia('(min-width: 1280px)').matches,
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 1280px)');
    const update = () => setIsWide(mediaQuery.matches);

    update();
    mediaQuery.addEventListener('change', update);

    return () => mediaQuery.removeEventListener('change', update);
  }, []);

  // 서버에서 받은 목록을 화면에 세울 순서(활성/비활성 통틀어 정렬)로 맞춰 둔다.
  const sortedGroups = useMemo(
    () => sortGroups(classGroups ?? []),
    [classGroups],
  );

  // 순서 변경 모드에서 드래그로 바꾼 임시 목록. 저장 버튼을 누르기 전까지는 서버에 보내지 않는다.
  // null 이면 순서 변경 모드가 아니다.
  const [draftGroups, setDraftGroups] = useState<LmsClassGroupType[] | null>(
    null,
  );
  const isReorderMode = draftGroups !== null;

  // 순서 저장 요청이 끝날 때까지 추가 드래그와 버튼 조작을 막는다.
  const [isSavingOrder, setIsSavingOrder] = useState(false);

  // 상세/수정 모달을 띄울 대상 반. null 이면 닫힌 상태.
  const [detailTarget, setDetailTarget] = useState<LmsClassGroupType | null>(
    null,
  );

  // 순서 변경 중에는 편집 중인 임시 목록을, 평소에는 서버 목록을 그린다.
  const rows = draftGroups ?? sortedGroups;

  const activeGroups = useMemo(() => rows.filter((g) => g.isActive), [rows]);
  const inactiveGroups = useMemo(() => rows.filter((g) => !g.isActive), [rows]);

  // 순서 변경 모드고, 저장 대기 중이 아니며, 활성 반이 2개 이상일 때만 순서를 바꿀 수 있다.
  const canReorder = isReorderMode && !isSavingOrder && activeGroups.length > 1;

  const sensors = useSensors(
    // 살짝 눌러서 생기는 오작동을 막으려고 8px 이동해야 드래그로 인식한다.
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  const enterReorderMode = () => setDraftGroups(sortedGroups);
  const exitReorderMode = () => setDraftGroups(null);

  // 순서를 바꿀 활성 반이 2개 미만이 되면 모드를 닫는다.
  useEffect(() => {
    if (isReorderMode && (loadFailed || activeGroups.length < 2)) {
      exitReorderMode();
    }
  }, [isReorderMode, loadFailed, activeGroups.length]);

  /**
   * 드래그로 바꾼 순서를 저장한다. 성공한 경우에만 순서 변경 모드를 빠져나간다.
   * 순서 저장 API 는 이 클래스의 반 id 를 빠짐없이(비활성 포함) 받아야 하므로 전체 목록을 보낸다.
   */
  const saveOrder = async () => {
    if (!draftGroups) return;

    setIsSavingOrder(true);
    try {
      await updateClassGroupOrder(classId, {
        classGroupIds: draftGroups.map((g) => g.id),
      });
      reloadClassGroups();
      exitReorderMode();
      dispatch(openAlert({ message: '반 순서를 저장했습니다.' }));
    } catch (err) {
      dispatch(openAlert({ message: getErrorMessages(err) }));
    } finally {
      setIsSavingOrder(false);
    }
  };

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;

    const from = activeGroups.findIndex(
      (g) => String(g.id) === String(active.id),
    );
    const to = activeGroups.findIndex((g) => String(g.id) === String(over.id));
    if (from === -1 || to === -1) return;

    // 활성 반만 순서를 바꾸고, 비활성 반은 뒤에 그대로 붙여 전체 목록을 다시 만든다.
    setDraftGroups([...arrayMove(activeGroups, from, to), ...inactiveGroups]);
  };

  const descriptionText = (classGroup: LmsClassGroupType) =>
    classGroup.description ? (
      <TextWinds
        type="content_body3"
        color="gray7"
        className="ml-2 whitespace-pre-wrap break-words"
      >
        {classGroup.description}
      </TextWinds>
    ) : (
      <TextWinds type="content_body3" color="gray5" className="ml-2">
        반 설명 없음
      </TextWinds>
    );

  /** 좁은 화면 조회 셀: 순번·이름·설명을 한 칸에 모아 보여준다. */
  const compactCell = (record: LmsClassGroupType, orderNo?: number) => (
    <div className="flex flex-col gap-1">
      <div className="flex flex-wrap items-center gap-1.5">
        {orderNo !== undefined && (
          <span className="select-none rounded bg-gray1 px-1.5 py-0.5 text-xs text-gray6">
            {orderNo}
          </span>
        )}
        <TextWinds
          type="content_body2"
          color="gray9"
          className="break-words font-medium"
        >
          {record.name}
        </TextWinds>
      </div>
      <div>{descriptionText(record)}</div>
    </div>
  );

  const nameColumn = (
    orderNoOf?: (record: LmsClassGroupType) => number | undefined,
  ): ColumnsType<LmsClassGroupType>[number] => ({
    // 본문 셀(ml-2)과 왼쪽을 맞추려고 헤더도 같은 만큼 들여쓴다.
    title: <span className="ml-2">반 이름</span>,
    key: 'name',
    width: isWide ? '30%' : undefined,
    render: (_, record) => {
      if (!isWide) return compactCell(record, orderNoOf?.(record));
      return (
        <TextWinds
          type="content_body2"
          color="gray9"
          className="ml-2 break-words font-semibold"
        >
          {record.name}
        </TextWinds>
      );
    },
  });

  const descriptionColumn: ColumnsType<LmsClassGroupType>[number] = {
    title: <span className="ml-2">반 설명</span>,
    key: 'description',
    render: (_, record) => descriptionText(record),
  };

  const manageColumn: ColumnsType<LmsClassGroupType>[number] = {
    title: '관리',
    key: 'action',
    align: 'center',
    width: isWide ? 96 : 72,
    render: (_, record) => (
      <ActionButton
        label="관리"
        icon={Cog6ToothIcon}
        color="purple"
        onClick={() => setDetailTarget(record)}
      />
    ),
  };

  // ── 활성 반 표 ──────────────────────────────────────────────────────
  const activeOrderNoOf = (record: LmsClassGroupType) => {
    const index = activeGroups.findIndex((g) => g.id === record.id);
    return index === -1 ? undefined : index + 1;
  };

  // 순서 열: 순서 변경 모드(드래그 손잡이) 또는 넓은 화면(순번)일 때 보인다.
  const showOrderColumn = isReorderMode || isWide;

  const activeColumns: ColumnsType<LmsClassGroupType> = [
    ...(showOrderColumn
      ? [
          {
            title: isWide ? '순서' : '',
            key: 'order',
            align: 'center' as const,
            width: isWide ? 64 : 40,
            render: (_: unknown, record: LmsClassGroupType) => (
              <div className="flex items-center justify-center gap-1">
                {isReorderMode && <DragHandle />}
                {isWide && (
                  <TextWinds type="content_body3" color="gray7">
                    {activeOrderNoOf(record)}
                  </TextWinds>
                )}
              </div>
            ),
          },
        ]
      : []),
    nameColumn(activeOrderNoOf),
    ...(isWide ? [descriptionColumn] : []),
    // 순서 변경 중에는 관리 버튼을 숨겨 정렬에만 집중하게 한다.
    ...(isReorderMode ? [] : [manageColumn]),
  ];

  // ── 비활성 반 표 ────────────────────────────────────────────────────
  const spacerColumn: ColumnsType<LmsClassGroupType>[number] = {
    key: 'spacer',
    width: 64,
    render: () => null,
  };

  const inactiveColumns: ColumnsType<LmsClassGroupType> = [
    // 활성 반 표의 순서 열과 좌측을 맞추기 위한 빈 칸.
    ...(isWide ? [spacerColumn] : []),
    nameColumn(),
    ...(isWide ? [descriptionColumn] : []),
    ...(isReorderMode ? [] : [manageColumn]),
  ];

  const sectionHeading = (
    label: string,
    count: number,
    hint?: string,
    action?: React.ReactNode,
  ) => (
    <div className="mb-2 mr-2 flex flex-col gap-y-1">
      {/* 첫 줄: 제목·개수(·넓은 화면에서는 안내문)·순서 변경 버튼 */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <TextWinds type="title_h4" color="gray9" className="font-semibold">
            {label}
          </TextWinds>
          <TextWinds type="content_body3" color="gray6">
            {count}개
          </TextWinds>
          {/* {hint && isWide && (
            <TextWinds type="content_body3" color="orange4">
              {hint}
            </TextWinds>
          )} */}
        </div>
        {action && <div className="shrink-0">{action}</div>}
      </div>
      {/* 좁은 화면에서는 안내문을 아래 줄로 내려 전체 너비로 보여준다. */}
      {/* {hint && !isWide && (
        <TextWinds type="content_body3" color="orange4">
          {hint}
        </TextWinds>
      )} */}
      {hint && (
        <TextWinds type="content_body3" color="orange4">
          {hint}
        </TextWinds>
      )}
    </div>
  );

  const totalCount = classGroups?.length ?? 0;

  // 좁은 화면에서는 이름·설명이 한 칸에 겹쳐 들어가므로 행 구분선(기본 #f0f0f0)이 흐리다.
  // gray4 로 진하게 올려 반과 반 사이가 또렷하게 보이도록 한다.
  const tableTheme = isWide
    ? {}
    : { components: { Table: { borderColor: '#CED4DA' } } };

  // 순서 변경은 활성 반에만 해당하므로 활성 반 영역 헤더에 둔다.
  // 순서 변경 모드에서는 드래그 결과를 저장/취소로 확정한다. (드래그할 때마다 저장하지 않는다)
  const reorderButton =
    !loadFailed &&
    activeGroups.length > 1 &&
    (isReorderMode ? (
      <div className="flex flex-row gap-2">
        <ActionButton
          label="취소"
          color="gray"
          onClick={exitReorderMode}
          disabled={isSavingOrder}
        />
        <ActionButton
          label={isSavingOrder ? '저장 중' : '순서 저장'}
          icon={CheckIcon}
          color="skyblue"
          variant="filled"
          onClick={saveOrder}
          disabled={isSavingOrder}
          // 라벨이 달라져도 버튼 너비가 흔들리지 않도록 고정한다.
          className="w-[88px]"
        />
      </div>
    ) : (
      <ActionButton
        label="순서 변경"
        icon={Bars3Icon}
        color="skyblue"
        onClick={enterReorderMode}
        className="w-[88px]"
      />
    ));

  return (
    <div className="flex min-h-0 w-full min-w-0 flex-col bg-white xl:h-full xl:flex-1 xl:rounded-lg xl:shadow-card">
      <div className="flex flex-row items-center justify-between px-4 py-3 xl:rounded-t-lg xl:px-6 xl:py-4">
        <TextWinds type="title_h3" color="gray9">
          등록된 반 수정/삭제
        </TextWinds>
        <TextWinds type="content_body3" color="gray7">
          전체 {classGroups ? `${totalCount}개` : ''}
        </TextWinds>
      </div>

      {/* 반 목록 */}
      <section className="flex min-h-0 min-w-0 flex-1 flex-col p-4 xl:p-6">
        {loadFailed ? (
          <div className="flex min-h-[160px] flex-col items-center justify-center gap-4">
            <TextWinds type="content_body2" color="gray7">
              반 목록을 불러오지 못했습니다.
            </TextWinds>
            <div className="w-[160px]">
              <HFbutton height={36} onClick={reloadClassGroups}>
                다시 시도
              </HFbutton>
            </div>
          </div>
        ) : totalCount === 0 && classGroups ? (
          <div className="flex min-h-[160px] items-center justify-center">
            <TextWinds type="content_body2" color="gray6">
              등록된 반이 없습니다.
            </TextWinds>
          </div>
        ) : (
          <div
            ref={scrollRef}
            className="min-h-0 min-w-0 flex-1 overflow-x-auto overflow-y-auto"
          >
            <ConfigProvider theme={tableTheme}>
              <div className="rounded-lg border border-gray3 bg-skyblue_5 p-4">
                {/* 활성 반 영역 */}
                {sectionHeading(
                  '활성 반',
                  activeGroups.length,
                  isReorderMode
                    ? '손잡이(≡)를 끌어 순서를 바꾼 뒤, 순서 저장 버튼을 눌러 저장해주세요.'
                    : '오른쪽 관리 버튼을 클릭해 반 정보를 수정·삭제하고, 배정된 학생을 확인할 수 있어요.',
                  reorderButton,
                )}
                <ListContext.Provider value={{ canReorder }}>
                  <DndContext
                    sensors={sensors}
                    modifiers={[restrictToVerticalAxis]}
                    onDragEnd={onDragEnd}
                  >
                    <SortableContext
                      items={activeGroups.map((g) => String(g.id))}
                      strategy={verticalListSortingStrategy}
                    >
                      <Table
                        columns={activeColumns}
                        dataSource={activeGroups}
                        rowKey="id"
                        components={{ body: { row: SortableRow } }}
                        pagination={false}
                        size="small"
                        tableLayout="fixed"
                        showHeader={isWide}
                        loading={!classGroups}
                        locale={{
                          emptyText: '활성 상태인 반이 없습니다.',
                        }}
                      />
                    </SortableContext>
                  </DndContext>
                </ListContext.Provider>
              </div>

              {/* 비활성 반 영역*/}
              {inactiveGroups.length > 0 && (
                <div className="mt-10 rounded-lg border border-gray3 bg-gray1 p-4">
                  {sectionHeading(
                    '비활성 반',
                    inactiveGroups.length,
                    `비활성 반에는 학생을 새로 배정할 수 없어요. 비활성 반에 속해있는 학생은 모두 '정지' 상태에요.`,
                  )}
                  <Table
                    columns={inactiveColumns}
                    dataSource={inactiveGroups}
                    rowKey="id"
                    pagination={false}
                    size="small"
                    tableLayout="fixed"
                    showHeader={isWide}
                  />
                </div>
              )}
            </ConfigProvider>
          </div>
        )}
      </section>

      {/* 반 관리 모달 */}
      <ClassGroupDetailDialog
        open={detailTarget !== null}
        classId={classId}
        classGroup={detailTarget}
        classGroups={classGroups}
        onClose={() => setDetailTarget(null)}
        reloadClassGroups={reloadClassGroups}
      />
    </div>
  );
};

export default ClassGroupListCard;
